// src/components/Map.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SpotListModal from '../../components/SpotListModal';
import StampListModal from '../../components/StampListModal';
import QuizModal from '../../components/QuizModal';
import InfoModal from '../../components/InfoModal';
import StampModal from '../../components/StampModal';
import InputQuizModal from '../../components/InputQuizModal';
import QuizResultModal from '../../components/QuizResultModal';
import ProximityModal from '../../components/ProximityModal';
import AudioPermissionModal from '../../components/AudioPermissionModal';
import CongratulationsModal from '../../components/CongratulationsModal';
import audioManager from '../../utils/AudioManager';
import '../../styles/Map.css';
import { startGuideSession, endGuideSession, saveLocation } from '../../utils/firebase';
import { LOCATION_UPLOAD_INTERVAL } from '../../config/firebase';
import spots from '../../settings/TamaChuoPark/spot.json';

// Leafletのデフォルトアイコン修正
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const defaultCenter = {
  lat: 34.677560,
  lng: 135.480345,
};

// UI/UX改善: フルスクリーン対応
const mapContainerStyle = {
  width: '100%',
  height: '100vh',
  minHeight: 'none',
  maxHeight: 'none'
};

// カテゴリ別カスタムアイコンの作成
function customPinIcon(category, isCompleted) {
  const iconMap = {
    'Guide': '/images/map_pin/guide-icon.svg',
    'Warning': '/images/map_pin/note-icon.svg',
    'Stamp': '/images/map_pin/stampspot-icon.svg',
    'Select_Quiz': '/images/map_pin/quiz-icon.svg',
    'Input_Quiz': '/images/map_pin/quiz-icon.svg',
    'Charge_Station': '/images/map_pin/battery-icon.svg',
  };

  // 完了済みの時に上書き
  if (isCompleted) {
    if (category === 'Stamp') {
      return L.icon({
        iconUrl: '/images/map_pin/stampget-icon.svg',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
      });
    }
    if (category === 'Select_Quiz' || category === 'Input_Quiz') {
      return L.icon({
        iconUrl: '/images/map_pin/quizclear-icon.svg',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
      });
    }
  }

  const iconUrl = iconMap[category] || '/images/map_pin/guide-icon.svg';

  return L.icon({
    iconUrl,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
}

const currentLocationIcon = L.icon({
  iconUrl: '/images/position-icon.svg',
  iconSize: [30, 30],
  iconAnchor: [15, 15]
});

// =====================
// エリア定義
// =====================

// エリア1：外に出てほしくないエリア（内側にとどまるべき範囲）
const areaMustStayIn = [
  [35.621377847892106,139.42389825928956],
  [35.619265312379525,139.4240905724178],
  [35.61928144940988,139.42433043948688],
  [35.61917386914652,139.42433705650814],
  [35.619224035497666,139.42600580136838],
  [35.61926173569009,139.4260034403327],
  [35.61927194381751,139.42607856568077],
  [35.61929770069217,139.426133009012],
  [35.61914753005533,139.42625735880807],
  [35.61912769491889,139.42634368872484],
  [35.61919901680153,139.42656958437482],
  [35.61942465514272,139.42659050340046],
  [35.6194537859743,139.4265294362625],
  [35.619551293001024,139.42647470499833],
  [35.61957683858968,139.4265541804134],
  [35.619908854668495,139.42659569835308],
  [35.61990695562558,139.42672598614297],
  [35.620019348925425,139.42692901124627],
  [35.62032952113954,139.42677882351046],
  [35.620458256645605,139.4270347971148],
  [35.620509602322755,139.42733204375338],
  [35.62046686628193,139.42744683300492],
  [35.62047388953759,139.42753077278763],
  [35.62053489532812,139.42753736895997],
  [35.62068714308724,139.4276510400976],
  [35.62080742694182,139.42782453965083],
  [35.621067899733774,139.4280451978243],
  [35.621062611665394,139.4278293283288],
  [35.6210758674502,139.42769065841065],
  [35.62110836990938,139.42760908176706],
  [35.621180281413956,139.42750155232676],
  [35.62125790329115,139.42755982821842],
  [35.62163016379361,139.42753894078635],
  [35.621748677419276,139.42761594578502],
  [35.62171615640784,139.42771412500815],
  [35.62157955623215,139.4278828542159],
  [35.621519789182386,139.4280597903268],
  [35.62172537777403,139.4280677207703],
  [35.62189505485186,139.4281283910684],
  [35.62189329515644,139.4282031702066],
  [35.6218566258416,139.42826667940523],
  [35.62217082270577,139.42857519022942],
  [35.622312007348285,139.4281522273841],
  [35.62270419919035,139.42838014274133],
  [35.62273161556385,139.42800888101482],
  [35.62270212793885,139.4277914545106],
  [35.62135920811835,139.42390622282323]
];

// エリア2：中に入ってほしくないエリア（立ち入り禁止範囲）
const areaMustStayOut = [
  [35.619673064375775, 139.42590130731622],
  [35.61955297760768, 139.42591286848972],
  [35.61955193337387, 139.4259655360587],
  [35.619442288775446, 139.42596425148457],
  [35.61944020030492, 139.42615308398956],
  [35.61953313716799, 139.4261543685637],
  [35.61953313716799, 139.42622630475518],
  [35.61968141823071, 139.4262275893314],
  [35.619673064375775, 139.42590130731622],
];

// ポリゴンスタイル
const polygonStyleStayIn = {
  fillColor: '#4285f4',
  fillOpacity: 0.1,
  color: '#4285f4',
  opacity: 0.8,
  weight: 2
};

const polygonStyleKeepOut = {
  fillColor: '#ff0000',
  fillOpacity: 0.15,
  color: '#ff0000',
  opacity: 0.9,
  weight: 2
};

// ピンロケーション（IDを追加）
const pinLocations = spots.map((spot, index) => ({
  ...spot,
  id: spot.id || `spot-${index}` // IDがない場合は生成
}));

// ★ スタンプ対象カテゴリ（スタンプ＋クイズも対象化）
const STAMPABLE_CATEGORIES = new Set(['Stamp', 'Select_Quiz', 'Input_Quiz']);

// カテゴリとモーダルタイプのマッピング
const categoryModalMap = {
  'Guide': 'info',
  'Warning': 'info',
  'Stamp': 'stamp',
  'Select_Quiz': 'quiz',
  'Input_Quiz': 'inputQuiz',
  'Charge_Station': 'info'
};

// マップ中心移動とズーム用のコンポーネント
function MapController({ center, shouldPanTo, targetZoom, resetZoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (shouldPanTo && center) {
      if (targetZoom && targetZoom !== map.getZoom()) {
        map.setView([center.lat, center.lng], targetZoom);
      } else {
        map.panTo([center.lat, center.lng]);
      }
    }
  }, [map, center, shouldPanTo, targetZoom]);
  
  useEffect(() => { if (resetZoom) { /* noop */ } }, [resetZoom]);
  
  return null;
}

// 現在地マーカーコンポーネント
function CurrentLocationMarker({ position, onClick, t }) {
  if (!position) return null;
  
  return (
    <Marker 
      position={[position.lat, position.lng]} 
      icon={currentLocationIcon}
      eventHandlers={{
        click: onClick
      }}
    >
      <Popup>{t('map.currentLocation')}</Popup>
    </Marker>
  );
}

function Map({ center = defaultCenter, zoom = 18 }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const LOCALE = i18n.language || 'ja';

  const [collectedStamps, setCollectedStamps] = useState(0);
  const [showSpotList, setShowSpotList] = useState(false);
  const [showStampList, setShowStampList] = useState(false);
  const [activeModal, setActiveModal] = useState({ type: null, data: null });
  const [quizResult, setQuizResult] = useState({ show: false, isCorrect: false });
  const [currentPosition, setCurrentPosition] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isFollowingLocation, setIsFollowingLocation] = useState(true);
  const [mapCenter, setMapCenter] = useState(center);
  const [proximityModal, setProximityModal] = useState({ show: false, spotInfo: null });
  const [dismissedSpots, setDismissedSpots] = useState(new Set());
  const [lastNearSpot, setLastNearSpot] = useState(null);
  const [showArea] = useState(true);

  // ★ エリア1・エリア2の状態
  const [isOutsideArea1, setIsOutsideArea1] = useState(false);  // エリア1の外にいる
  const [outsideArea1Timer, setOutsideArea1Timer] = useState(null);
  const [isInsideArea2, setIsInsideArea2] = useState(false);    // エリア2の中にいる
  const [insideArea2Timer, setInsideArea2Timer] = useState(null);

  const [targetZoom, setTargetZoom] = useState(null);
  const [isSpotMoving, setIsSpotMoving] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionStartedRef = useRef(false);
  const [lastPlayedSpot, setLastPlayedSpot] = useState(null);
  const [showAudioPermission, setShowAudioPermission] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completedStampIds, setCompletedStampIds] = useState(new Set());
  const [stamps, setStamps] = useState([]);
  
  const watchIdRef = useRef(null);
  const locationTimerRef = useRef(null);

  // 多言語対応のテキスト取得関数
  const getLocalizedText = useCallback((textObj, fallbackLocale = 'ja') => {
    if (typeof textObj === 'string') return textObj;
    if (!textObj) return '';
    return textObj[LOCALE] || textObj[fallbackLocale] || textObj.ja || textObj.en || textObj.zh || textObj.ko || '';
  }, [LOCALE]);

  // 音声パス取得関数（多言語対応）
  const getAudioPath = useCallback((spot) => {
    if (!spot) return null;
    if (spot.voice && spot.voice[LOCALE]) {
      return spot.voice[LOCALE];
    }
    if (spot.voice && spot.voice.ja) {
      return spot.voice.ja;
    }
    return null;
  }, [LOCALE]);

  // スポットデータの生成（多言語対応）
  const spotData = spots.map((spot, i) => ({
    id: i + 1,
    name: getLocalizedText(spot.title),
    description: getLocalizedText(spot.description),
    image: spot.thumbnail,
    category: spot.category,
  }));

  // 初期化時にスタンプデータを設定
  useEffect(() => {
    const savedStamps = localStorage.getItem('completedStamps');
    if (savedStamps) {
      const savedIds = new Set(JSON.parse(savedStamps));
      setCompletedStampIds(savedIds);
      setCollectedStamps(savedIds.size);
      
      // クリア判定はスタンプ対象カテゴリで
      const total = pinLocations.filter(p => STAMPABLE_CATEGORIES.has(p.category)).length;
      if (savedIds.size === total && total > 0) {
        setShowCongratulations(true);
      }
    }
  
    // スタンプ一覧（スタンプ対象カテゴリのみ）
    const initialStamps = pinLocations
      .filter(pin => STAMPABLE_CATEGORIES.has(pin.category))
      .map(pin => ({
        id: pin.id,
        name: getLocalizedText(pin.title),
        completed: false,
        category: pin.category,
      }));
    setStamps(initialStamps);
  }, [getLocalizedText]);

  // スタンプ取得処理（クイズ正解/スタンプ取得に共通）
  const handleStampGet = useCallback((spotId) => {
    if (completedStampIds.has(spotId)) {
      return;
    }
  
    const newCompletedIds = new Set(completedStampIds);
    newCompletedIds.add(spotId);
    setCompletedStampIds(newCompletedIds);
  
    setStamps(prevStamps => 
      prevStamps.map(stamp => 
        stamp.id === spotId 
          ? { ...stamp, completed: true }
          : stamp
      )
    );
  
    const newCount = newCompletedIds.size;
    setCollectedStamps(newCount);
  
    const totalStamps = pinLocations.filter(p => STAMPABLE_CATEGORIES.has(p.category)).length;
    if (newCount === totalStamps && totalStamps > 0) {
      setTimeout(() => {
        setShowCongratulations(true);
      }, 1000);
    }
  
    localStorage.setItem('completedStamps', JSON.stringify([...newCompletedIds]));
  }, [completedStampIds]);

  const closeCongratulations = () => {
    setShowCongratulations(false);
  };

  const stampsWithStatus = stamps.map(stamp => ({
    ...stamp,
    completed: completedStampIds.has(stamp.id)
  }));

  // 音声許可処理
  const handleAudioAllow = useCallback(async () => {
    try {
      if (!window.audioContextForApp) {
        window.audioContextForApp = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (window.audioContextForApp.state === 'suspended') {
        await window.audioContextForApp.resume();
      }
      const osc = window.audioContextForApp.createOscillator();
      const g = window.audioContextForApp.createGain();
      osc.connect(g);
      g.connect(window.audioContextForApp.destination);
      g.gain.value = 0;
      osc.start();
      osc.stop(window.audioContextForApp.currentTime + 0.05);
    } catch (e) {
      console.error('AudioContext setup failed:', e);
    }
    setAudioEnabled(true);
    setShowAudioPermission(false);
  }, []);

  const handleAudioDeny = useCallback(() => {
    setAudioEnabled(false);
    setShowAudioPermission(false);
  }, []);

  // LeafletのCSS読み込み
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  // ポリゴン内判定関数（polygon は [lat, lng] 前提）
  const isPointInPolygon = useCallback((point, polygon) => {
    if (!polygon || polygon.length === 0) return false;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      // polygon[i][0] = lat, polygon[i][1] = lng
      if (((polygon[i][0] > point.lat) !== (polygon[j][0] > point.lat)) &&
          (point.lng < (polygon[j][1] - polygon[i][1]) * (point.lat - polygon[i][0]) / 
           (polygon[j][0] - polygon[i][0]) + polygon[i][1])) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  // =====================
  // エリア境界チェック
  //   - エリア1: 外に出たら NG
  //   - エリア2: 中に入ったら NG
  // =====================
  const checkAreaBoundary = useCallback(() => {
    if (!currentPosition || !showArea) return;

    const inArea1 = areaMustStayIn.length > 0 && isPointInPolygon(currentPosition, areaMustStayIn);
    const inArea2 = areaMustStayOut.length > 0 && isPointInPolygon(currentPosition, areaMustStayOut);

    // --- エリア1: 外に出たら警告 ---
    if (areaMustStayIn.length > 0 && !inArea1) {
      if (!isOutsideArea1) {
        setIsOutsideArea1(true);

        const timer = setTimeout(() => {
          // 翻訳キーが未定義でも動くように fallback メッセージも指定
          alert(t('map.alerts.outsideArea1', '移動可能エリアの外に出ています'));
          setOutsideArea1Timer(null);
        }, 10000);

        setOutsideArea1Timer(timer);
      }
    } else {
      if (isOutsideArea1) {
        setIsOutsideArea1(false);
        if (outsideArea1Timer) {
          clearTimeout(outsideArea1Timer);
          setOutsideArea1Timer(null);
        }
      }
    }

    // --- エリア2: 中に入ったら警告 ---
    if (areaMustStayOut.length > 0 && inArea2) {
      if (!isInsideArea2) {
        setIsInsideArea2(true);

        const timer = setTimeout(() => {
          alert(t('map.alerts.insideForbiddenArea', '立ち入り禁止エリアに入っています'));
          setInsideArea2Timer(null);
        }, 10000);

        setInsideArea2Timer(timer);
      }
    } else {
      if (isInsideArea2) {
        setIsInsideArea2(false);
        if (insideArea2Timer) {
          clearTimeout(insideArea2Timer);
          setInsideArea2Timer(null);
        }
      }
    }
  }, [
    currentPosition,
    showArea,
    isPointInPolygon,
    t,
    isOutsideArea1,
    outsideArea1Timer,
    isInsideArea2,
    insideArea2Timer
  ]);

  // 距離計算関数
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const isWithinProximity = useCallback((spot) => {
    if (!currentPosition || !spot) return false;
    const d = calculateDistance(
      currentPosition.lat,
      currentPosition.lng,
      spot.latitude,
      spot.longitude
    );
    // spot.radius があれば優先。なければ 100m を既定に
    const threshold = spot.radius || 100;
    return d <= threshold;
  }, [currentPosition, calculateDistance]);

  // 近接チェック
  const checkProximity = useCallback(async () => {
    if (!currentPosition) return;

    const PROXIMITY_THRESHOLD = 100;

    let closestPin = null;
    let closestDistance = Infinity;

    pinLocations.forEach(pin => {
      const distance = calculateDistance(
        currentPosition.lat,
        currentPosition.lng,
        pin.latitude,
        pin.longitude
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPin = pin;
      }
    });

    if (closestPin && closestDistance <= PROXIMITY_THRESHOLD) {
      if (!dismissedSpots.has(closestPin.id)) {
        const currentModalSpotId = proximityModal.spotInfo?.id;
        const isNewSpot = currentModalSpotId !== closestPin.id;
        
        if (isNewSpot || !proximityModal.show) {
          setProximityModal({
            show: true,
            spotInfo: {
              id: closestPin.id,
              title: getLocalizedText(closestPin.title),
              description: getLocalizedText(closestPin.summary || closestPin.description),
              image: closestPin.thumbnail,
              icon: closestPin.category === 'Guide' ? '📍' : 
                    closestPin.category === 'Warning' ? '⚠️' :
                    closestPin.category === 'Stamp' ? '🏆' : '❓'
            }
          });
          
          if (lastPlayedSpot !== closestPin.id) {
            const audioPath = getAudioPath(closestPin);
            if (audioPath && audioEnabled) {
              try {
                await window.audioContextForApp?.resume();
              } catch {}
              audioManager.playSimple(audioPath);
              setLastPlayedSpot(closestPin.id);
            }
          }
        }
        setLastNearSpot(closestPin.id);
      }
    } else {
      if (lastNearSpot) {
        setLastNearSpot(null);
        setLastPlayedSpot(null);
        if (proximityModal.show) {
          setProximityModal({ show: false, spotInfo: null });
        }
      }
    }
  }, [currentPosition, calculateDistance, dismissedSpots, lastNearSpot, getLocalizedText, lastPlayedSpot, getAudioPath, audioEnabled, proximityModal.show, proximityModal.spotInfo?.id]);

  // 音声許可後の再生処理
  useEffect(() => {
    (async () => {
      const id = proximityModal.spotInfo?.id;
      if (!audioEnabled || !proximityModal.show || !id) return;
      if (lastPlayedSpot === id) return;
      
      const pin = pinLocations.find((p) => p.id === id);
      const audioPath = pin ? getAudioPath(pin) : null;
      if (!audioPath) return;
      
      try {
        await window.audioContextForApp?.resume();
      } catch {}
      audioManager.playSimple(audioPath);
      setLastPlayedSpot(id);
    })();
  }, [audioEnabled, proximityModal.show, proximityModal.spotInfo?.id, lastPlayedSpot, getAudioPath]);

  // 現在地取得
  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('map.errors.geolocationNotSupported'));
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPosition = {
          lat: latitude,
          lng: longitude
        };
        setCurrentPosition(newPosition);
        setLocationError(null);
        
        if (isFollowingLocation) {
          setMapCenter(newPosition);
        }
      },
      (error) => {
        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('map.errors.permissionDenied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('map.errors.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('map.errors.timeout');
            break;
          default:
            errorMessage = t('map.errors.unknownError');
            break;
        }
        setLocationError(errorMessage);
      },
      options
    );
  }, [isFollowingLocation, t]);

  // 位置追従開始
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('map.errors.geolocationNotSupported'));
      return null;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPosition = {
          lat: latitude,
          lng: longitude
        };
        setCurrentPosition(newPosition);
        setLocationError(null);
        
        if (isFollowingLocation) {
          setMapCenter(newPosition);
        }
      },
      (error) => {
        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('map.errors.permissionDenied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('map.errors.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('map.errors.timeout');
            break;
          default:
            errorMessage = t('map.errors.unknownError');
            break;
        }
        setLocationError(errorMessage);
      },
      options
    );

    return watchIdRef.current;
  }, [isFollowingLocation, t]);

  // ガイドセッション開始
  useEffect(() => {
    // まだ現在地が取れていない場合は何もしない
    if (!currentPosition) return;
  
    // すでにセッション開始処理を走らせている場合は何もしない
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true; // ★ ここで「もう実行済み」フラグを立てる
  
    const initializeSession = async () => {
      try {
        const newSessionId = await startGuideSession(currentPosition);
        setSessionId(newSessionId);
        console.log('Guide session started:', newSessionId);
      } catch (error) {
        console.error('Failed to start guide session:', error);
      }
    };
  
    initializeSession();
  }, [currentPosition]); // ★ sessionId 依存は不要

  // 位置情報の定期送信
  useEffect(() => {
    if (!sessionId || !currentPosition) return;

    const sendLocationPeriodically = () => {
      saveLocation(sessionId, {
        lat: currentPosition.lat,
        lng: currentPosition.lng,
        accuracy: currentPosition.accuracy || null
      });
    };

    sendLocationPeriodically();

    locationTimerRef.current = setInterval(
      sendLocationPeriodically, 
      LOCATION_UPLOAD_INTERVAL * 1000
    );

    return () => {
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
      }
    };
  }, [sessionId, currentPosition]);

  // エフェクト
  useEffect(() => {
    getCurrentPosition();
  }, [getCurrentPosition]);

  useEffect(() => {
    if (currentPosition?.lat && currentPosition?.lng) {
      const tid = setTimeout(() => {
        checkProximity();
      }, 500);
      return () => clearTimeout(tid);
    }
  }, [currentPosition, checkProximity]);

  useEffect(() => {
    checkAreaBoundary();
  }, [checkAreaBoundary]);

  useEffect(() => {
    if (isFollowingLocation) {
      startLocationTracking();
    } else {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    }

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
      }
      audioManager.stop();
    };
  }, [isFollowingLocation, startLocationTracking]);

  useEffect(() => {
    return () => {
      if (outsideArea1Timer) {
        clearTimeout(outsideArea1Timer);
      }
      if (insideArea2Timer) {
        clearTimeout(insideArea2Timer);
      }
    };
  }, [outsideArea1Timer, insideArea2Timer]);

  // イベントハンドラー
  const handleGuideEnd = async () => {
    try {
      audioManager.stop();
      
      if (sessionId) {
        await endGuideSession(sessionId, currentPosition);
        console.log('Guide session ended');
      }
      
      if (locationTimerRef.current) {
        clearInterval(locationTimerRef.current);
        locationTimerRef.current = null;
      }
      
      navigate('/tamachuopark/returntutorial');
    } catch (error) {
      console.error('Failed to end guide session:', error);
      navigate('/tamachuopark/returntutorial');
    }
  };

  const handleSpotList = () => {
    setShowSpotList(true);
  };

  const handleStampList = () => {
    setShowStampList(true);
  };

  const handleModeSwitch = () => {
    const newFollowingMode = !isFollowingLocation;
    setIsFollowingLocation(newFollowingMode);
    
    if (newFollowingMode && currentPosition) {
      setMapCenter(currentPosition);
      alert(t('map.alerts.trackingModeOn'));
    } else {
      alert(t('map.alerts.trackingModeOff'));
    }
  };

  const closeSpotList = () => {
    setShowSpotList(false);
  };

  const closeStampList = () => {
    setShowStampList(false);
  };

  const closeActiveModal = () => {
    setActiveModal({ type: null, data: null });
    setTargetZoom(null);
  };

  // SpotListModalからのスポットクリック処理
  const handleSpotClick = (spot) => {
    const spotPin = pinLocations.find(pin => 
      getLocalizedText(pin.title) === spot.name
    );
    
    if (spotPin) {
      const spotPosition = { lat: spotPin.latitude, lng: spotPin.longitude };
      
      setIsSpotMoving(true);
      setMapCenter(spotPosition);
      setTargetZoom(18);
      
      if (isFollowingLocation) {
        setIsFollowingLocation(false);
      }
      
      setTimeout(() => {
        setIsSpotMoving(false);
        setTargetZoom(null);
      }, 1500);

      const modalType = categoryModalMap[spotPin.category] || 'info';
      
      // クイズデータの構造を整形
      let modalData = {
        id: spotPin.id,
        name: getLocalizedText(spotPin.title),
        image: spotPin.thumbnail,
        description: getLocalizedText(spotPin.description),
        audioPath: getAudioPath(spotPin),
        category: spotPin.category,
        completed: completedStampIds.has(spotPin.id),
        isNearby: isWithinProximity(spotPin), // ★ 追加
      };
      
      // Select_Quizの場合、データ構造を調整
      if (spotPin.category === 'Select_Quiz' && spotPin.data) {
        modalData = {
          ...modalData,
          title: spotPin.data.title,
          grid: spotPin.data.grid,
          summary: spotPin.summary,
          question: spotPin.data.question || spotPin.summary,
          answers: spotPin.data.choices,
          answer: spotPin.data.answer
        };
      }
      // Input_Quizの場合
      else if (spotPin.category === 'Input_Quiz' && spotPin.data) {
        modalData = {
          ...modalData,
          ...spotPin.data
        };
      }
      // その他のカテゴリ
      else if (spotPin.data) {
        modalData = {
          ...modalData,
          ...spotPin.data
        };
      }
      
      setActiveModal({
        type: modalType,
        data: modalData
      });
    }
  };

  const closeQuizResult = () => {
    setQuizResult({ show: false, isCorrect: false });
  };

  // ProximityModal閉じる処理
  const closeProximityModal = useCallback(() => {
    const id = proximityModal.spotInfo?.id;
    if (id !== null && id !== undefined) {
      setDismissedSpots((prev) => new Set(prev).add(id));
    }
    setLastPlayedSpot(null);
    setProximityModal({ show: false, spotInfo: null });
  }, [proximityModal.spotInfo?.id]);

  // ProximityModalクリック処理
  const handleProximityModalClick = (spotId) => {
    const pin = pinLocations.find(p => p.id === spotId);
    if (pin) {
      setProximityModal({ show: false, spotInfo: null });
      
      const modalType = categoryModalMap[pin.category] || 'info';
      
      // クイズデータの構造を整形
      let modalData = {
        id: pin.id,
        name: getLocalizedText(pin.title),
        image: pin.thumbnail,
        description: getLocalizedText(pin.description),
        audioPath: getAudioPath(pin),
        category: pin.category,
        completed: completedStampIds.has(pin.id),
        isNearby: isWithinProximity(pin), // ★ 追加
      };
      
      // Select_Quizの場合、データ構造を調整
      if (pin.category === 'Select_Quiz' && pin.data) {
        modalData = {
          ...modalData,
          title: pin.data.title,
          grid: pin.data.grid,
          summary: pin.summary,
          question: pin.data.question || pin.summary,
          answers: pin.data.choices,
          answer: pin.data.answer
        };
      }
      // Input_Quizの場合
      else if (pin.category === 'Input_Quiz' && pin.data) {
        modalData = {
          ...modalData,
          ...pin.data
        };
      }
      // その他のカテゴリ
      else if (pin.data) {
        modalData = {
          ...modalData,
          ...pin.data
        };
      }
      
      setActiveModal({
        type: modalType,
        data: modalData
      });
    }
  };

  // ピン直接クリック処理
  const handlePinClick = (pin) => {
    if (isSpotMoving) return;
    
    setTargetZoom(null);
    setIsSpotMoving(false);
    
    const modalType = categoryModalMap[pin.category] || 'info';
    
    // クイズデータの構造を整形
    let modalData = {
      id: pin.id,
      name: getLocalizedText(pin.title),
      image: pin.thumbnail,
      description: getLocalizedText(pin.description),
      audioPath: getAudioPath(pin),
      category: pin.category,
      completed: completedStampIds.has(pin.id),
      isNearby: isWithinProximity(pin), // ★ 追加
    };
    
    // Select_Quizの場合、データ構造を調整
    if (pin.category === 'Select_Quiz' && pin.data) {
      modalData = {
        ...modalData,
        title: pin.data.title,
        grid: pin.data.grid,
        summary: pin.summary,
        question: pin.data.question || pin.summary,
        answers: pin.data.choices,
        answer: pin.data.answer
      };
    }
    // Input_Quizの場合
    else if (pin.category === 'Input_Quiz' && pin.data) {
      modalData = {
        ...modalData,
        ...pin.data
      };
    }
    // その他のカテゴリ
    else if (pin.data) {
      modalData = {
        ...modalData,
        ...pin.data
      };
    }
    
    setActiveModal({
      type: modalType,
      data: modalData
    });
  };

  const returnToMap = () => {
    // カウント更新は handleStampGet に集約済み
    closeQuizResult();
    closeActiveModal();
  };

  // 正解結果の表示。正解時はスタンプ獲得＆再挑戦不可化
  const showQuizResult = (isCorrect) => {
    setQuizResult({ show: true, isCorrect });
    if (isCorrect && (activeModal.type === 'quiz' || activeModal.type === 'inputQuiz')) {
      const spotId = activeModal.data?.id;
      if (spotId) {
        handleStampGet(spotId);
        // モーダル内 completed 状態を即時反映
        setActiveModal(prev => ({
          ...prev,
          data: prev?.data ? { ...prev.data, completed: true } : prev.data
        }));
      }
    }
  };

  const handleCurrentPositionClick = () => {
    if (currentPosition) {
      alert(`${t('map.currentLocation')}\n緯度: ${currentPosition.lat.toFixed(6)}\n経度: ${currentPosition.lng.toFixed(6)}`);
    } else {
      alert(t('map.alerts.gettingLocation', '現在地を取得中です'));
    }
  };

  return (
    <div className="map-wrapper">
      <div className="map-container">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={zoom}
          style={mapContainerStyle}
          className="leaflet-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapController 
            center={mapCenter} 
            shouldPanTo={isFollowingLocation || isSpotMoving} 
            targetZoom={targetZoom}
            resetZoom={false}
          />
          
          {/* スポットマーカー */}
          {pinLocations.map((pin, index) => {
            const isCompleted = completedStampIds.has(pin.id);
            return (
              <Marker
                key={pin.id || index}
                position={[pin.latitude, pin.longitude]}
                icon={customPinIcon(pin.category, isCompleted)}
                eventHandlers={{
                  click: (e) => {
                    e.target.closePopup();
                    handlePinClick(pin);
                  }
                }}
              >
                {!activeModal.type && (
                  <Popup>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0' }}>{getLocalizedText(pin.title)}</h3>
                      <p style={{ margin: 0, fontSize: '12px' }}>
                        {getLocalizedText(pin.summary || pin.description)}
                      </p>
                    </div>
                  </Popup>
                )}
              </Marker>
            );
          })}
          
          {/* 現在地マーカー */}
          <CurrentLocationMarker 
            position={currentPosition} 
            onClick={handleCurrentPositionClick}
            t={t}
          />

          {/* エリア1・エリア2のポリゴン */}
          {showArea && (
            <>
              {areaMustStayIn.length > 0 && (
                <Polygon
                  positions={areaMustStayIn}
                  pathOptions={polygonStyleStayIn}
                />
              )}
              {areaMustStayOut.length > 0 && (
                <Polygon
                  positions={areaMustStayOut}
                  pathOptions={polygonStyleKeepOut}
                />
              )}
            </>
          )}
        </MapContainer>
      </div>
      
      <button className="guide-end-button" onClick={handleGuideEnd}>
        {t('map.buttons.endGuide')}
      </button>
      
      <div className="stamp-counter">
        <span className="stamp-label">{t('map.stamps.label', 'スタンプ獲得数')}</span>
        <span className="stamp-count">
          <span className="current-count">{collectedStamps}</span>
          <span className="total-count"> / {pinLocations.filter(p => STAMPABLE_CATEGORIES.has(p.category)).length}</span>
        </span>
      </div>
      
      {locationError && (
        <div className="location-error">
          <span>{locationError}</span>
          <button
            onClick={() => navigate('/tamachuopark/locationsetting')}
            className="retry-button"
            aria-label={t('map.buttons.retry')}
          >
            {t('map.buttons.retry')}
          </button>
        </div>
      )}

      {/* エリア1外側警告 */}
      {isOutsideArea1 && showArea && (
        <div className="area-warning">
          <span>⚠️ {t('map.alerts.outsideArea1', '移動可能エリアの外に出ています')}</span>
        </div>
      )}

      {/* エリア2内側警告 */}
      {isInsideArea2 && showArea && (
        <div className="area-warning">
          <span>⚠️ {t('map.alerts.insideForbiddenArea', '立ち入り禁止エリアに入っています')}</span>
        </div>
      )}
      
      <div className="bottom-buttons-container">
        <button className="bottom-button" onClick={handleSpotList}>
          {t('map.buttons.spotList')}
        </button>
        <button className="bottom-button" onClick={handleStampList}>
          {t('map.buttons.stampList', 'スタンプ一覧')}
        </button>
        <button className="bottom-button" onClick={handleModeSwitch}>
          {t('map.buttons.modeSwitch')}
        </button>
      </div>

      <SpotListModal 
        isOpen={showSpotList}
        onClose={closeSpotList}
        spots={spotData}
        onSpotClick={handleSpotClick}
      />

      <StampListModal 
        isOpen={showStampList}
        onClose={closeStampList}
        stamps={stampsWithStatus}
        onStampClick={handleSpotClick}
      />

      <QuizModal
        isOpen={activeModal.type === 'quiz'}
        onClose={closeActiveModal}
        quiz={activeModal.data}
        onShowResult={showQuizResult}
      />

      <InfoModal
        isOpen={activeModal.type === 'info'}
        onClose={closeActiveModal}
        info={activeModal.data}
      />

      <StampModal
        isOpen={activeModal.type === 'stamp'}
        onClose={closeActiveModal}
        stamp={activeModal.data}
        onStampGet={handleStampGet}
      />

      <InputQuizModal
        isOpen={activeModal.type === 'inputQuiz'}
        onClose={closeActiveModal}
        quiz={activeModal.data}
        onShowResult={showQuizResult}
      />

      <QuizResultModal
        isOpen={quizResult.show}
        onClose={closeQuizResult}
        onReturnToMap={returnToMap}
        isCorrect={quizResult.isCorrect}
      />

      <ProximityModal
        isOpen={proximityModal.show}
        onClose={closeProximityModal}
        spotInfo={proximityModal.spotInfo}
        onModalClick={handleProximityModalClick}
      />

      <AudioPermissionModal
        isOpen={showAudioPermission}
        onAllow={handleAudioAllow}
        onDeny={handleAudioDeny}
      />

      <CongratulationsModal
        isOpen={showCongratulations}
        onClose={closeCongratulations}
      />
    </div>
  );
}

export default Map;
